import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { forkJoin, Observable } from "rxjs";
import { AppConfig } from "src/app/app-config.service";
import { AcumaticaRequestOptions, AcumaticaService } from "../acumatica.service";
import { AcumaticaEntity, AcumaticaEntityService } from "./acumatica-entity";
import { AcumaticaLoadingDialogComponent } from "../loading-indicator/loading-dialog-indicator.component";
import { KitSpecification, KitSpecificationService, KitStockComponent } from "./KitSpecification.service";
import { KitAuditType, KitAudit, KitAuditSet, KitAuditItem, KitAuditNotice, KitAuditNotice_InpersistentMessage } from "src/app/screens/kit-audit/kit-audit";
import { Router } from "@angular/router";
import { StockItem, StockItemService } from "./StockItem.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Acumatica } from "../acumatica-field-decorator";
import { TitleCasePipe } from "@angular/common";
import { Customer, CustomerSampleBook, CustomerSerivce } from "./Customer.service";
import { AlertDialog } from "src/app/common/alert-dialog/alert-dialog.component";
import { ISampleCardBinder } from "src/app/models/IAppConfig";

/** how many days to use the cached binder contents */
const BinderContentsStorageTimeInDays = 1;
const BinderContentsStorageKey = 'sample-card-binder-contents';
const KitAuditStorageKey = 'kit-audit-data';
const BinderContentMissingFromAppConfigMsg = "Sample Card Binders list not found in App Configuration.\nPlease refresh your browser and contact I.T. if the problem persists.";

@Injectable({
    providedIn: 'root'
})
export class KitAuditService extends KitSpecificationService {    
    constructor(
        private router: Router,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        protected acumatica: AcumaticaService,
        private collatedSetInquiry: CollatedSetByItemService,
        private stockItems: StockItemService,
        private customers: CustomerSerivce,
    ) {
        super(acumatica);

        // try loading any in-progress Kit Audit operation from local storage
        let data = localStorage.getItem(KitAuditStorageKey);
        if(data != null) {
            try { this._audit = KitAudit.deserialize(data); }
            catch { localStorage.removeItem(KitAuditStorageKey); }
        }
    }

    /** the current Kit-Audit operation's data model instance (plain, serializable object) */
    public get audit(): KitAudit | undefined { return this._audit; };
    public set audit(value: KitAudit | undefined) { this._audit = value; this.Persist(); }
    private _audit?: KitAudit;

    /** provides a persistent flag indicating if any Empty Binder Shells are being placed by the rep and the customer location */
    public get anyBinderShellsPlaced(): boolean { return this._audit?.anyBinderShellsPlaced || false; }
    public set anyBinderShellsPlaced(value: boolean) { this._audit!.anyBinderShellsPlaced = value; this.Persist(); }    

    /** starts an Audit operation on the give kit and navigates to the corresponding screen */
    public AuditKitContents(kit: KitAuditSet, autoCheckInventoryID?: string) {
        this.audit!.activeKit = kit;
        this.Persist();
        this.autoCheckInventoryID = autoCheckInventoryID;
        this.router.navigate(['kit-audit', 'audit']);
    }

    /** indicates an Inventory ID to be checked automatically upon entering the Audit Kit component process (the Memo item that was used to start the collated set audit) */
    public autoCheckInventoryID?: string;

    /** interface to the standard Sample Card Binder list defined in the build's App.Config */
    public get SampleCardBinders(): ISampleCardBinder[] | undefined {
        return AppConfig.settings?.SampleCardBinders || undefined;
    }

    /** persists the current state of the kit audit operation object model to device storage */
    public Persist() {
        let data = this.audit?.serialize();
        if(data === undefined) localStorage.removeItem(KitAuditStorageKey);
        else localStorage.setItem(KitAuditStorageKey, data);
    }

    /** starts a new Kit Audit of the specified type. Returns a Promise that is resolved when
     * the Audit operations is ready (binder contents async loaded) */
    public StartNewAudit(type: KitAuditType): Promise<void> {
        return new Promise((resolve, reject) => {
            
            switch(type) {
                case KitAuditType.Binders:
                    this.InitBinderAudit()
                    .then(audit => { this.audit = audit; resolve(); })
                    .catch(reject);
                    break;
                case KitAuditType.LooseCards:
                    this.InitLooseCardAudit()
                    .then(audit => { this.audit = audit; resolve(); })
                    .catch(reject);
                    break;
                case KitAuditType.CollatedSets:
                    // Collated Set contents will be async fetched in the 'Select Kit' step
                    this.audit = new KitAudit(KitAuditType.CollatedSets);
                    resolve();
                    break;
            }
        });
    }

    /** explicitly reload the standard Binder contents (invalidating local storage cache) */
    public RefreshBinderContents(): Promise<{[BinderID: string]: BinderKitSpecification}> {
        localStorage.removeItem(BinderContentsStorageKey);
        return this.GetCurrentBinderContents();
    }

    /** async fetch the Card items within each of the standard Binders (using local storage cache) */
    public GetCurrentBinderContents(): Promise<{[BinderID: string]: BinderKitSpecification}> {        
        return new Promise((resolve, reject) => {

            // the list of Sample Card Binders is missing from the app.config file -- likely user needs refresh
            if(this.SampleCardBinders === undefined) return reject(BinderContentMissingFromAppConfigMsg);
            
            // check the device's local storage for the binder contents being cached & stored
            let cache = localStorage.getItem(BinderContentsStorageKey);
            if(cache != null) {
                try {
                    let cacheData = JSON.parse(cache);
                    let asof = new Date(cacheData.asof);
                    let diff = Math.abs(new Date().getTime() - asof.getTime());
                    let diffDays = Math.ceil(diff / (1000 * 3600 * 24));
                    if(diffDays >= BinderContentsStorageTimeInDays) {
                        resolve(cacheData.contents);
                        return;
                    }
                }
                catch {}
            }

            // create a user feedback async loading dialog indicator
            let dialog_ref = this.dialog.open(AcumaticaLoadingDialogComponent, {
                disableClose: true,
                data: {message: `Getting Latest Binder Contents\n(0 / ${this.SampleCardBinders.length})`}
            });

            // async fetch each of the standard Binders and wait for all to complete
            let binderContents: {[BinderID: string]: KitSpecification} = {};
            let doneCount: number = 0;
            let titlecasePipe = new TitleCasePipe();
            let getters = this.SampleCardBinders.map(binder => new Observable<void>(subscriber => {
                this.get(binder.KitID + '\\' + binder.Revision, {loader:null}).subscribe(kit => {
                    dialog_ref.componentInstance.message = `Getting Latest Binder Contents\n(${++doneCount} / ${this.SampleCardBinders!.length})`

                    let binderKit = <BinderKitSpecification>kit;

                    // delete irrelevant fields that do not need written/read in local storage
                    delete binderKit.__id;
                    delete binderKit.__rowNumber;                    
                    binderKit.StockComponents.forEach((component: any) => {
                        delete component.__id;
                        delete component.__rowNumber;
                        delete component.ComponentQty;
                    });

                    // take the Empty Binder Shell item out of the components list
                    binderKit.EmptyBinderShell = binderKit.StockComponents.find(component => component.ItemClassID == 'EBND');                     
                    if(binderKit.EmptyBinderShell) {
                        binderKit.EmptyBinderShell.Description =
                            titlecasePipe.transform(
                                (' ' + binderKit.EmptyBinderShell.Description + ' ').replace(' EMPTY ', '').trim()
                            );
                        binderKit.StockComponents.remove(binderKit.EmptyBinderShell);
                    }

                    // modify item descriptions for purposes of binder audit
                    binderKit.StockComponents.forEach(component => {
                        component.Description = (' ' + component.Description + ' ')
                            .replace(' CARD ', '')
                            .replace('  ', ' ')
                            .trim();
                    });
                    
                    binderContents[binder.KitID] = binderKit;
                    subscriber.next();
                    subscriber.complete();
                });
            }));

            forkJoin(getters).subscribe(() => {
                dialog_ref.close();                
                resolve(binderContents);

                // store the binder contents in local storage cache
                let cacheData = {
                    asof: new Date(),
                    contents: binderContents
                };
                localStorage.setItem(BinderContentsStorageKey, JSON.stringify(cacheData));
            });
        });
    }

    /** initialize a new KitAudit with the standard Binders and their contained cards */
    private InitBinderAudit(): Promise<KitAudit> {
        return new Promise((resolve, reject) => {
            // get the Acumatica KitSpecifications for each binder
            this.GetCurrentBinderContents()
            .then(kitSpecifications => {
                // create the new KitAudit object
                let kitAudit = new KitAudit(KitAuditType.Binders,
                    // mapping the Binders into KitAuditSets
                    this.SampleCardBinders!.map(binder =>
                        new KitAuditSet(binder.Description,
                            kitSpecifications[binder.KitID].StockComponents
                                // create KitAuditItem objects from the card stock components
                                .map(KitAuditItem.fromKitStockComponent)
                                // sort the Card components alphabetically
                                .sort((a, b) => a.Description!.localeCompare(b.Description!)),
                            binder.KitID
                        )
                    )
                );

                // initialize the Placed Empty Binder Shell flags
                this.SampleCardBinders!
                .filter(binder => kitSpecifications[binder.KitID].EmptyBinderShell != undefined)
                .forEach(binder => kitAudit.placedEmptyShells[kitSpecifications[binder.KitID].EmptyBinderShell!.InventoryID] = false);

                resolve(kitAudit);
            })
            .catch(reject);
        });
    }

    /** initialize a new KitAudit with the standard Binders contents grouped by Loose Type */
    private InitLooseCardAudit(): Promise<KitAudit> {
        return new Promise((resolve, reject) => {
            // get the Acumatica KitSpecifications for each binder
            this.GetCurrentBinderContents()
            .then(kitSpecifications => {
                // initialize an dictionary to combine card items into
                let kitSets: {[type:string]: KitAuditItem[]} = {}
                // iterate the standard card binders
                this.SampleCardBinders!.forEach(binder => {
                    // initialize, if necessary, a loose-card collection object
                    if( kitSets[binder.LooseType] === undefined ) kitSets[binder.LooseType] = [];
                    // add the cards for the binder to the item array
                    kitSets[binder.LooseType] = kitSets[binder.LooseType].concat(
                        kitSpecifications[binder.KitID].StockComponents
                            .map(KitAuditItem.fromKitStockComponent)
                            .sort((a, b) => a.Description!.localeCompare(b.Description!))
                    );
                });

                // create & resolve with a KitAudit object
                let kits = Object.keys(kitSets).map(set => new KitAuditSet(set, kitSets[set]));
                let kitAudit = new KitAudit(KitAuditType.LooseCards, kits);
                resolve(kitAudit);
            })
            .catch(reject);
        });
    }

    /** initialize a new KitAuditSet based on the given scan input value (memo item) */
    public InitCollatedSetAudit(value: string): Promise<KitAuditSet> {
        return new Promise<KitAuditSet>((resolve, reject) => {            
            this.collatedSetInquiry.get(value, {loader:"Getting Collated Set Contents\n" + value}).subscribe(response => {
                if(response.Details.length == 0) {
                    reject("A Collated Set kit could not be found for \"" + value + "\"");
                    return;
                }

                let items: KitAuditItem[] = [];    
                response.Details.forEach((detail: CollatedSetByItemDetail) => {
                    items.push(KitAuditItem.fromCollatedSetGenericInquiry(detail));
                });
                                
                resolve(new KitAuditSet(response.Details[0].KitDescription, items));
            });
        });
    }

    /** registeres the provided notice object into the audit operation's list of notices */
    public RegisterNotice(notice: KitAuditNotice) {
        // prevent multiple duplicate notices
        let n1 = JSON.stringify(notice);
        if(this.audit!.notices.reduce<boolean>((duplicate, n) => duplicate || JSON.stringify(n) == n1, false)) return;

        // register the new notice
        this.audit!.notices.push(notice); this.Persist();
    }

    /** looks up a stock item from Acumatica with cancellable snackbar feedback  */
    public LookupItemFromAcumatica(InventoryID: string): Promise<StockItem> {
        return new Promise<StockItem>((resolve, reject) => {
            // open a snackbar feedback for the user
            let cancelled = false;
            let snackRef = this.snackBar.open("Looking up item " + InventoryID, 'cancel');
            snackRef.onAction().subscribe(() => cancelled = true);

            // lookup the item in Acumatica
            this.stockItems.getSimple(InventoryID, {loader:null}).subscribe(
                item => {
                    if(cancelled) return;
                    snackRef.dismiss();
                    resolve(item);
                },
                err => {
                    if(cancelled) return;
                    snackRef.dismiss();
                    reject(err);
                    this.RegisterNotice(
                        new KitAuditNotice_InpersistentMessage("Invalid Inventory ID \"" + InventoryID + "\"")
                    );
                }
            )
        })
    }

    /** sanitize a scanner/input value and return the contained Inventory ID (depending on current audit type) */
    public ScanValueToInventoryID(scanValue: string | object): Promise<string> {        
        return new Promise((resolve, reject) => {

            // extract Inventory ID from stock tag JSON QR strings
            if(scanValue instanceof Object) {
                let o = <any>scanValue;
                scanValue = o.ITM || o.ITEM || o.InventoryID || undefined;
                if(scanValue === undefined) {
                    reject("No Inventory ID could be found in the data\n" + JSON.stringify(scanValue));
                    return;
                }
            }

            scanValue = (<string>scanValue).trim().toUpperCase();

            // scanned a QR hyperlink to the website?
            // try to parse out an Inventory ID from the URL
            if(scanValue.startsWith("HTTP://") || scanValue.startsWith("HTTPS://")) {
                let pos = scanValue.indexOf('/ITEM/');
                if(pos == -1) {
                    // QR link may be to homepage if item is not available on website and thus won't contain an /item/..
                    reject("The website link does not specify an Inventory Item");
                    return;
                }
                // crop start of URL upto and including "../item/"
                scanValue = scanValue.substring(pos + 6);
                // crop query-string (analytics tracking)
                pos = scanValue.indexOf('?'); if(pos != -1) scanValue.substring(0, pos);
                // crop a trailing "/" at end of URL
                if(scanValue.endsWith('/')) scanValue = scanValue.substring(0, scanValue.length - 1);
            }

            resolve(scanValue);
        });
    }

    /** create a Customer Order for any missing component items */
    public CreateMissingComponentOrder(): void {
        this.router.navigate(['order-entry', 'audit'])
    }

    /** returns an array of KitAuditItems that are missing from the audited (touched) kits */
    public GetMissingComponentItems(): KitAuditItem[] {
        return this.audit!.kits
            .filter(kit => kit.AnyChecked())
            .map(kit => kit.Unchecked())
            .flat();
    }

    /** returns an array of the Full Binder Inventory IDs for the list of Empty Binder Shell IDs */
    public MapEmptyShellIDsToBinderIDs(emptyShellIDs: string[]): Promise<string[]> {
        return new Promise<string[]>(resolve => {
            this.GetCurrentBinderContents().then(binders => {
                // build a hashset of { [Empty Shell ID]: [Full Binder ID], .. }
                let map: { [shellID: string]: string } = {};
                Object.keys(binders)
                    .filter(id => binders[id].EmptyBinderShell)
                    .forEach(id => map[binders[id].EmptyBinderShell!.InventoryID] = id);
                // map the empty shell IDs to their full IDs
                let binderIDs = emptyShellIDs.map(shellID => map[shellID]);
                resolve(binderIDs);
            })
        });
    }

    /** updates the Sample Book data records for the specified Customer based on the audited book items
     * Returns an immediately resolved Promise if there is nothing to update
     */
    public UpdateCustomerSampleBooks(CustomerID: string): Promise<void> {
        if(!this.audit
            || this.audit.type != KitAuditType.Binders
            || !this.audit.AnyChecked()) return Promise.resolve();

        return new Promise((resolve, reject) => {

            // get Customer's current sample book data
            this.customers.get(CustomerID!, {
                expand: 'SampleBooks',
                loader: "Getting Current Sample Books",
                noCache: true,
            }).subscribe(currentCustomer => {

                // create a new minimal Customer object to update sample books
                let customerUpdate: { CustomerID: string, SampleBooks: CustomerSampleBook[] }
                    = { CustomerID: CustomerID, SampleBooks: [] };

                let BuildUpdatedBinderIDsList = new Promise<string[]>(resolve => {
                    // create a list of Binders Inventory IDs that the user has audited (Checked)
                    let updateBinderIDs: string[] =
                        this.audit!.kits.filter(kit => kit.AnyChecked()).map(kit => kit.KitInventoryID!);
                    
                    // append to that list, any included empty binder shells
                    if(this.audit!.anyBinderShellsPlaced) {
                        // need to map the Empty Binder Shells (as they are on the Order) to their full Binder Book IDs
                        this.MapEmptyShellIDsToBinderIDs(this.audit!.placedEmptyShellsList()).then(placedBinderIDs => {
                            // and combine those lists
                            resolve([...updateBinderIDs, ...placedBinderIDs].unique());
                        });
                    } else {
                        resolve(updateBinderIDs);
                    }
                });

                BuildUpdatedBinderIDsList.then(updateBinderIDs => {
                    // for all of the audited or newly placed binders
                    updateBinderIDs.forEach(binderID => {
                        // look for the new/audited binder in the customer's existing list of binders
                        let sampleBook = currentCustomer.SampleBooks!.find(book => book.InventoryID == binderID);

                        // if the customer does not already have this binder
                        if (sampleBook === undefined) {
                            // create a new entry for it in the customer's sample books
                            sampleBook = new CustomerSampleBook({
                                Active: true,
                                InventoryID: binderID
                            });
                        }
                        // update the audited LastCheck date/time
                        sampleBook.LastChecked = new Date();
                        customerUpdate.SampleBooks.push(sampleBook);
                    });

                    // update the customer audit info
                    this.customers.put(<Customer>customerUpdate, { loader: null }).subscribe(
                        () => resolve(),
                        err => reject()
                    );

                });
            });
        });
    }
}

@Injectable({providedIn:'root'})
export class CollatedSetByItemService extends AcumaticaEntityService {
    constructor(
        protected acumatica: AcumaticaService
    ) {
        super(acumatica, 'CollatedSetByItem', CollatedSetByItemInquiry);
    }

    get(InventoryID: string, options?: AcumaticaRequestOptions): Observable<CollatedSetByItemInquiry> {
        options = {...options, expand: 'CollatedSetByItemDetails'};
        let inquiry = new CollatedSetByItemInquiry(InventoryID);
        return this.putEntity(inquiry, options);
    }
}

export class CollatedSetByItemInquiry extends AcumaticaEntity {
    @Acumatica('InventoryID') InventoryID: string;
    @Acumatica('CollatedSetByItemDetails') Details: CollatedSetByItemDetail[] = [];

    constructor(InventoryID: string) {
        super();
        this.InventoryID = InventoryID;
    }
}
export class CollatedSetByItemDetail extends AcumaticaEntity {
    @Acumatica() KitInventoryID!: string;
    @Acumatica() KitDescription!: string;
    @Acumatica() InventoryID!: string;
    @Acumatica() Description!: string;
    @Acumatica() LifeCycle: string | null = null;
}

export class BinderKitSpecification extends KitSpecification {
    public EmptyBinderShell?: KitStockComponent;
}